// Driver-side notifications — real API client.
//
// Notifications are generated server-side from real product events (new glove
// box document, service logged, diagnostic report ready, shop estimate/
// appointment/repair-status updates, …) and stored in the `notifications`
// table. The sidebar badge and the Notifications page read from these helpers
// (via state lifted into ChatLayout) so they always stay in sync.
//
// Backend (services/user_details.py, port 8001):
//   GET  /api/notifications/{identity}                 -> { notifications, unread_count }
//   GET  /api/notifications/{identity}/unread-count    -> { unread_count }
//   POST /api/notifications/{identity}/{id}/read       -> mark one read
//   POST /api/notifications/{identity}/read-all        -> mark all read

import { getUserDetailsBaseUrl } from './chatIdentity';

function base(identity) {
    return `${getUserDetailsBaseUrl()}/api/notifications/${encodeURIComponent(identity)}`;
}

// Returns { notifications: [...], unreadCount: N }. Each notification has:
// id, category, title, message, icon, tone, link, isRead, timestamp.
export async function fetchNotifications(identity, { limit = 50 } = {}) {
    // `ok` reports whether the server was actually reachable, so callers can drive a
    // connectivity indicator. Existing callers that destructure only notifications/unreadCount
    // are unaffected.
    if (!identity) return { notifications: [], unreadCount: 0, ok: true };
    try {
        const res = await fetch(`${base(identity)}?limit=${limit}`, { cache: 'no-store' });
        if (!res.ok) return { notifications: [], unreadCount: 0, ok: false };
        const data = await res.json();
        return {
            notifications: Array.isArray(data?.notifications) ? data.notifications : [],
            unreadCount: Number(data?.unread_count) || 0,
            ok: true,
        };
    } catch (_) {
        return { notifications: [], unreadCount: 0, ok: false };
    }
}

export async function fetchUnreadCount(identity) {
    if (!identity) return 0;
    try {
        const res = await fetch(`${base(identity)}/unread-count`, { cache: 'no-store' });
        if (!res.ok) return 0;
        const data = await res.json();
        return Number(data?.unread_count) || 0;
    } catch (_) {
        return 0;
    }
}

export async function markNotificationReadApi(identity, notificationId) {
    if (!identity || notificationId == null) return false;
    try {
        const res = await fetch(`${base(identity)}/${notificationId}/read`, { method: 'POST' });
        return res.ok;
    } catch (_) {
        return false;
    }
}

// One-time (idempotent) generation of notifications from the user's existing
// history, so the feed isn't empty for events that predate the feature.
export async function backfillNotifications(identity) {
    if (!identity) return 0;
    try {
        const res = await fetch(`${base(identity)}/backfill`, { method: 'POST' });
        if (!res.ok) return 0;
        const data = await res.json();
        return Number(data?.created) || 0;
    } catch (_) {
        return 0;
    }
}

export async function markAllNotificationsReadApi(identity) {
    if (!identity) return false;
    try {
        const res = await fetch(`${base(identity)}/read-all`, { method: 'POST' });
        return res.ok;
    } catch (_) {
        return false;
    }
}

// Soft-delete (dismiss) a single notification so it leaves the feed.
export async function dismissNotificationApi(identity, notificationId) {
    if (!identity || notificationId == null) return false;
    try {
        const res = await fetch(`${base(identity)}/${notificationId}/dismiss`, { method: 'POST' });
        return res.ok;
    } catch (_) {
        return false;
    }
}

// "Oct 6, 2026 · 1:37 PM"
export function formatNotificationTime(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${date} · ${time}`;
}
